import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import { WHATSAPP_URL } from '../../config/constants';

const FloatingWidgets = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'bot',
      text: 'Hello! Welcome to ISPL Automation Consulting. How can we assist with your technology or staffing needs today?',
      time: 'Just now',
    },
  ]);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);

  const whatsappUrl = WHATSAPP_URL;

  useEffect(() => {
    if (isChatOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isChatOpen]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    const userMsg = {
      id: Date.now(),
      sender: 'user',
      text: inputText,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMsg]);
    const currentInput = inputText;
    setInputText('');

    // Instant automated response simulation
    setTimeout(() => {
      const botMsg = {
        id: Date.now() + 1,
        sender: 'bot',
        text: `Thank you for your message! An ISPL automation consultant has received your inquiry: "${currentInput}". We will reply shortly. You can also click "Schedule Consultation" above for direct scheduling!`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, botMsg]);
    }, 800);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3 select-none">
      
      {/* Interactive Chat Panel */}
      {isChatOpen && (
        <div className="w-80 sm:w-96 bg-[#0B142D] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[420px] animate-in slide-in-from-bottom-4 duration-200">
          {/* Chat Header */}
          <div className="bg-[#060D21] border-b border-slate-800 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold text-xs">
                  ISPL
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-[#060D21]" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-white leading-tight">ISPL Automation Support</h4>
                <span className="text-[10px] text-amber-400 font-medium">Online • Stored & Encrypted</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsChatOpen(false)}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <Icon name="X" size={16} />
            </button>
          </div>

          {/* Chat Messages Body */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-[#070E22]/60">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-xs leading-relaxed ${
                    msg.sender === 'user'
                      ? 'bg-amber-500 text-slate-950 font-semibold rounded-br-none'
                      : 'bg-slate-900 border border-slate-800 text-slate-200 rounded-bl-none'
                  }`}
                >
                  {msg.text}
                </div>
                <span className="text-[9px] text-slate-500 mt-1 px-1">{msg.time}</span>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Chat Input Footer */}
          <form onSubmit={handleSendMessage} className="p-3 bg-[#060D21] border-t border-slate-800 flex items-center gap-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Ask ISPL team a question..."
              className="flex-1 px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60"
            />
            <button
              type="submit"
              className="w-9 h-9 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 flex items-center justify-center transition-colors flex-shrink-0"
            >
              <Icon name="Send" size={16} />
            </button>
          </form>
        </div>
      )}

      {/* Floating Buttons Column (Stacked Vertically) */}
      <div className="flex flex-col items-center gap-2.5">
        
        {/* WhatsApp Button (Standard Green #25D366) */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white flex items-center justify-center shadow-lg hover:scale-110 transition-all duration-200 group"
          title="Chat on WhatsApp"
        >
          <Icon name="MessageCircle" size={24} className="group-hover:rotate-12 transition-transform" />
        </a>

        {/* Chat Widget Toggle Button (Navy & Gold ISPL Theme) */}
        <button
          type="button"
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="w-12 h-12 rounded-full bg-[#060D21] border border-amber-400/60 text-amber-400 hover:bg-[#0B142D] flex items-center justify-center shadow-xl hover:scale-110 transition-all duration-200 group"
          title="Chat with Support"
        >
          <Icon
            name={isChatOpen ? 'X' : 'MessageSquare'}
            size={22}
            className="group-hover:rotate-12 transition-transform"
          />
        </button>
      </div>

    </div>
  );
};

export default FloatingWidgets;
